import React, { useCallback, useEffect, useMemo } from "react";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions, getBaseUnits, getUnitOptions, getFirstOption } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getUdefWindowCodeEmbodiedCarbonDisplay } from "utils/enclosure/embc/udefWindowLayers";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Field from "components/Field";
import Accordion from "components/Accordion";
import AccordionActions from "../../AccordionActions";
import { GradeDisplay, PercentageDisplay } from "components/EmbodiedCarbonGrade";
import EmbodiedCarbonEditor from "components/EmbodiedCarbonGrade/UdefEmbodiedCarbonEditor";

import classes from "features/Model/Enclosure/UserDefinedCodes/style.module.scss";

const typeValidation = getValidation("udefWindowLegacyType");
const centreGlassValidation = getValidation("udefWindowLegacyCentreOfGlassRVal");
const edgeGlassValidation = getValidation("udefWindowLegacyEdgeOfGlassRVal");
const frameRValValidation = getValidation("udefWindowLegacyFrameRVal");
const frameHeightValidation = getValidation("udefWindowLegacyFrameHeight");
const shgcValidation = getValidation("udefWindowLegacySHGC");
const frameMaterialValidation = getValidation("csWindowFrameMaterial");
const glazingValidation = getValidation("udefWindowOverallGlazingType");

const WindowLegacy = React.memo(({ id, change, accessor, drawerAccessor, barMetric = "percentage" }) => {
    const dispatch = useDispatch();
    const centreGlassUnits = useStateSelector(`${accessor}.rsiValues.centreOfGlass_u`);
    const edgeGlassUnits = useStateSelector(`${accessor}.rsiValues.edgeOfGlass_u`);
    const frameRValUnits = useStateSelector(`${accessor}.rsiValues.frame_u`);
    const legacyLayers = useStateSelector(`${drawerAccessor}.uDefCode.layers.windowLegacy`);
    const { id: glazingTypeId = 0 } = useStateSelector(`${accessor}.glazingType`) || {};
    const frameMaterial = useStateSelector(`${accessor}.frameMaterial`) || {};
    const frameHeightUnits = useStateSelector(`${accessor}.frameHeight_u`);
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const deleteLayer = useCallback(() => {
        const { [id]: _, ...remainingLayers } = legacyLayers;
        change(`${drawerAccessor}.uDefCode.layers.windowLegacy`, remainingLayers);
        dispatch(removeFormErrors(accessor));
    }, [id, legacyLayers, change, dispatch, drawerAccessor, accessor]);

    const layer =
        Object.keys(legacyLayers).map((key) => ({
            ...legacyLayers[key],
            id: key,
            type: "windowLegacy",
        }))[0] || {};

    const display = useMemo(() => {
        return getUdefWindowCodeEmbodiedCarbonDisplay({ layers: layer });
    }, [legacyLayers]);

    const showGrade = barMetric === "grade";

    const gradeInner = (
        <GradeDisplay
            emissions={display.emissions}
            storage={display.storage}
            emissionGrade={display.emissionGrade}
            storageGrade={display.storageGrade}
            withBorder={true}
            borderHeight={"100%"}
            borderColor={"#E1EAF0"}
            customGap={"0.5rem"}
            customPadding={"0 7px"}
            hasMismatch={display.hasMismatch}
        />
    );

    const percentageInner = (
        <PercentageDisplay
            emissions={display.emissions}
            storage={display.storage}
            withBorder={true}
            borderHeight={"100%"}
            borderColor={"#E1EAF0"}
            customGap={"0.5rem"}
            customPadding={"0 7px"}
            hasMismatch={display.hasMismatch}
        />
    );

    const frameOpts = useMemo(
        () => getOptions({ fieldKey: "csWindowFrameMaterial", indKey: glazingTypeId }),
        [glazingTypeId]
    );

    useEffect(() => {
        // Reset frame material if option is not in the frame options list.
        const optionMatches = frameOpts.filter(
            (opt) => opt.value.value === frameMaterial.value && opt.value.id === frameMaterial.id
        );

        if (isEmpty(optionMatches)) {
            change(
                `${accessor}.frameMaterial`,
                getFirstOption({ indKey: glazingTypeId, depKey: "csWindowFrameMaterial" })
            );
        }
    }, [frameMaterial, glazingTypeId, frameOpts, change]);

    return (
        <div>
            <Accordion
                className={classes.accordion}
                initOpenState={true}
                heading={
                    <span className={classes.accordionHeader}>
                        <span className={classes.accordionTitle}>Legacy Inputs</span>
                    </span>
                }
                hoverActions={<AccordionActions accessor={accessor} deleteLayer={deleteLayer} />}
                bodyClassName={classes.accordionBodyContent}
                carbonGrade={
                    <span className={classes.gradeContainer}>{showGrade ? gradeInner : percentageInner}</span>
                }
            >
                <div className={classes.inputFields}>
                    <InputRow gridTemplate="1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.type`}
                            options={getOptions({ fieldKey: "udefWindowLegacyType" })}
                            label="Window Type"
                            placeholder="Choose Window Type"
                            validate={typeValidation}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.glazingType`}
                            label="Glazing Type"
                            placeholder="Choose Glazing Type"
                            options={getOptions({ fieldKey: "udefWindowOverallGlazingType" })}
                            validate={glazingValidation}
                        />
                        <Field
                            component={Select}
                            name={`${accessor}.frameMaterial`}
                            label="Frame Material"
                            placeholder="Choose Frame Material"
                            options={frameOpts}
                            validate={frameMaterialValidation}
                        />
                    </InputRow>
                    <h4>R-Values</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.rsiValues.centreOfGlass`}
                            label="Centre of Glass"
                            placeholder={0.0}
                            validate={centreGlassValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("udefWindowLegacyCentreOfGlassRVal")}
                            change={change}
                            units={{
                                base: getBaseUnits("udefWindowLegacyCentreOfGlassRVal", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: centreGlassUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.rsiValues.centreOfGlass_u`,
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.rsiValues.edgeOfGlass`}
                            label="Edge of Glass"
                            placeholder={0.0}
                            validate={edgeGlassValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("udefWindowLegacyEdgeOfGlassRVal")}
                            change={change}
                            units={{
                                base: getBaseUnits("udefWindowLegacyEdgeOfGlassRVal", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: edgeGlassUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.rsiValues.edgeOfGlass_u`,
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.rsiValues.frame`}
                            label="Frame"
                            placeholder={0.0}
                            validate={frameRValValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("udefWindowLegacyFrameRVal")}
                            change={change}
                            units={{
                                base: getBaseUnits("udefWindowLegacyFrameRVal", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: frameRValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.rsiValues.frame_u`,
                            }}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.frameHeight`}
                            label="Frame Height"
                            placeholder={0.0}
                            validate={frameHeightValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("udefWindowLegacyFrameHeight")}
                            change={change}
                            units={{
                                base: getBaseUnits("udefWindowLegacyFrameHeight", modelUnits),
                                options: getUnitOptions("length"),
                                selected: frameHeightUnits,
                                unitType: "length",
                                accessor: `${accessor}.frameHeight_u`,
                            }}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.shgc`}
                            label="SHGC"
                            placeholder={0.0}
                            validate={shgcValidation}
                            decimals={getDecimalPlaces("udefWindowLegacySHGC")}
                        />
                    </InputRow>
                </div>
                <EmbodiedCarbonEditor
                    change={change}
                    display={display}
                    layersFieldPrefix={accessor}
                    layerType={"windowLegacy"}
                    layer={layer}
                />
            </Accordion>
        </div>
    );
});

export default WindowLegacy;
